import { toast } from '@workspace/ui/components/sonner';
import { useCallback, useState } from 'react';

import { shortUUID } from '~/utils/uuid';
import type { AttachedFile } from './types';

const maxFiles = 5;
const maxFileSize = 10 * 1024 * 1024; // 10MB

export function useChatFiles() {
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);

  const handleFileSelect = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);

      const validSizeFiles: File[] = [];
      const tooBigFiles: File[] = [];

      for (const file of fileArray) {
        if (file.size <= maxFileSize) {
          validSizeFiles.push(file);
        } else {
          tooBigFiles.push(file);
        }
      }

      if (tooBigFiles.length > 0) {
        toast.error(
          `Algunos archivos exceden el tamaño máximo de ${maxFileSize / (1024 * 1024)}MB`,
        );
      }

      if (attachedFiles.length + validSizeFiles.length > maxFiles) {
        toast.error(`Solo puedes adjuntar hasta ${maxFiles} archivos`);
        return;
      }

      const newFiles: AttachedFile[] = validSizeFiles.map((file) => ({
        id: shortUUID(),
        file,
        preview: file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : undefined,
      }));

      setAttachedFiles((prev) => [...prev, ...newFiles]);
    },
    [attachedFiles.length],
  );

  const removeFile = useCallback((id: string) => {
    setAttachedFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === id);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const clearFiles = useCallback(() => {
    for (const file of attachedFiles) {
      if (file.preview) URL.revokeObjectURL(file.preview);
    }
    setAttachedFiles([]);
  }, [attachedFiles]);

  return {
    attachedFiles,
    handleFileSelect,
    removeFile,
    clearFiles,
  };
}
